import { createTheme, Theme } from '@mui/material/styles';

// Define base light theme first (without components)
export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    common: {
      white: '#FFFFFF',
      black: '#1A1B20',
    },
    primary: {
      main: '#0058C1',
      light: '#D8E2FF',
      dark: '#001A42',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#0C1B36',
      light: '#CDE5FF',
      dark: '#001D32',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#FF6B6B',
      light: '#FFDAD6',
      dark: '#410002',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#5FAE5E',
      light: '#E0FFDF',
      dark: '#013D00',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#F2A517',
      light: '#FFEFD1',
      dark: '#4A3307',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#0058C1',
      light: '#D8E2FF',
      dark: '#002959',
      contrastText: '#FFFFFF',
    },
    grey: {
      50: '#FFFFFF',
      100: '#F7F9FF',
      200: '#D0D0D6',
      300: '#B8B9C3',
      400: '#E8E7EF',
      500: '#E2E2E9',
      600: '#898A90',
      700: '#44474F',
      800: '#1A1B20',
      900: '#0A0A0A',
    },
    text: {
      primary: '#1A1B20',
      secondary: '#898A90',
      disabled: '#D1D4D9',
    },
    divider: '#D1D4D9',
    background: {
      paper: '#FFFFFF',
      default: '#FFFFFF',
    },
    action: {
      active: '#0058C1',
      hover: '#E8F1F6',
      selected: '#C6D8EE',
      disabled: '#898a90',
      disabledBackground: '#D1D4D9',
      focus: '#C6D8EE',
    },
  },
  typography: {
    fontFamily: 'Montserrat',
    htmlFontSize: 16,
    fontSize: 14,
    fontWeightLight: 400,
    fontWeightRegular: 500,
    fontWeightMedium: 600,
    fontWeightBold: 700,
    h1: {
      fontFamily: 'Montserrat',
      fontSize: '6rem',
      lineHeight: 'auto',
      letterSpacing: 0,
    },
    h2: {
      fontFamily: 'Montserrat',
      fontSize: '3.75rem',
      lineHeight: 'auto',
      letterSpacing: 0,
    },
    h3: {
      fontFamily: 'Montserrat',
      fontSize: '3rem',
      lineHeight: 'auto',
      letterSpacing: 0,
    },
    h4: {
      fontFamily: 'Montserrat',
      fontSize: '2.125rem',
      lineHeight: 'auto',
      letterSpacing: 0,
    },
    h5: {
      fontFamily: 'Montserrat',
      fontSize: '1.5rem',
      lineHeight: 'auto',
      letterSpacing: 0,
    },
    h6: {
      fontFamily: 'Montserrat',
      fontSize: '1.25rem',
      lineHeight: 'auto',
      letterSpacing: 0,
    },
    subtitle1: {
      fontFamily: 'Montserrat',
      fontSize: '1rem',
      lineHeight: 'auto',
      letterSpacing: 0,
    },
    subtitle2: {
      fontFamily: 'Montserrat',
      fontSize: '.875rem',
      lineHeight: 'auto',
      letterSpacing: 0,
    },
    body1: {
      fontFamily: 'Montserrat',
      fontSize: '1rem',
      lineHeight: 'auto',
      letterSpacing: 0,
    },
    body2: {
      fontFamily: 'Montserrat',
      fontSize: '.875rem',
      lineHeight: 'auto',
      letterSpacing: 0,
    },
    caption: {
      fontFamily: 'Montserrat',
      fontSize: '.75rem',
      lineHeight: 'auto',
      letterSpacing: 0,
    },
    overline: {
      fontFamily: 'Montserrat',
      fontSize: '.75rem',
      lineHeight: 'auto',
      letterSpacing: 0,
    },
  },
});

// Create base dark theme (without components)
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    common: lightTheme.palette.common,
    primary: {
      main: '#90D3FC',
      light: '#CCECFF',
      dark: '#3E7EA6',
      contrastText: '#002858',
    },
    secondary: {
      main: '#A7C6FF',
      light: '#CDE5FF',
      dark: '#084B72',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#FF4C4C',
      light: '#FFDAD6',
      dark: '#410002',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#5FAE5E',
      light: '#DFFFDF',
      dark: '#013D00',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#F2A517',
      light: '#FFEFD1',
      dark: '#4A3307',
      contrastText: '#FFFFFF',
    },
    info: lightTheme.palette.info,
    grey: {
      50: '#F6F6F6',
      100: '#101418',
      200: '#C5C6D0',
      300: '#37393E',
      400: '#33353A',
      500: '#282A2F',
      600: '#161A1E',
      700: '#111518',
      800: '#0B0F12',
      900: '#101418',
    },
    text: {
      primary: '#F6F6F6',
      secondary: '#D0D0D0',
      disabled: '#6C6C6C',
    },
    divider: '#6C6C6C',
    background: {
      paper: '#37393E',
      default: '#37393E',
    },
    action: {
      active: '#90D3FC',
      hover: '#1E2E3A',
      selected: '#274354',
      disabled: '#6C6C6C',
      disabledBackground: '#101418',
      focus: '#274354',
    },
  },
  typography: lightTheme.typography,
});

// Function to generate component styles based on theme
const getThemeComponentStyles = (theme: Theme) => ({
  MuiAlert: {
    styleOverrides: {
      root: {
        maxWidth: '420px',
        maxHeight: '75px',
        fontSize: '.875rem',
        fontWeight: 500,
        color: theme.palette.text.secondary,
        padding: '10px',
        margin: 0,
        backgroundColor:
          theme.palette.mode === 'dark'
            ? theme.palette.grey[300]
            : theme.palette.common.white,
      },
      icon: {
        alignItems: 'center',
        display: 'flex',
        padding: 0,
        margin: '0 10px 0 0',
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: '46px',
        fontWeight: 600,
        fontFamily: theme.typography.fontFamily,
        textTransform: 'none' as const,
        boxShadow: 'none',
        letterSpacing: 0,
        '&:hover': {
          boxShadow: 'none',
        },
      },
      // Size overrides to match your custom component
      sizeSmall: {
        fontSize: '10px',
        height: '24px',
        padding: '6px 16px',
      },
      sizeMedium: {
        fontSize: '12px',
        height: '34px',
        padding: '10px 20px',
      },
      sizeLarge: {
        fontSize: '14px',
        height: '44px',
        padding: '14px 24px',
      },
      // Contained variant styles
      contained: {
        '&:disabled': {
          backgroundColor: theme.palette.action.disabledBackground,
          color: theme.palette.action.disabled,
        },
      },
      // Text variant styles
      text: {
        borderRadius: '4px',
        backgroundColor: 'transparent',
        padding: '8px !important',
        height: 'auto !important',
        minHeight: 'unset !important',
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
        '&:disabled': {
          color: theme.palette.action.disabled,
        },
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      root: {
        '& .MuiBackdrop-root': {
          backdropFilter: 'blur(5px)',
        },
      },
      paper: {
        borderRadius: '12px',
        padding: '24px 24px',
        backgroundImage: 'none', // Remove MUI dark mode elevation overlay
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontSize: theme.typography.h5.fontSize,
        fontWeight: theme.typography.fontWeightBold,
        color: theme.palette.text.primary,
        textAlign: 'center' as const,
        padding: '0 0 16px 0',
        fontFamily: theme.typography.fontFamily,
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '16px 0 0 0',
        justifyContent: 'center',
        '&.MuiDialogActions-spacing > :not(:first-of-type)': {
          marginLeft: 0,
        },
      },
    },
  },
  MuiFormControl: {
    styleOverrides: {
      root: {
        width: '100%',
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      root: {
        fontSize: '.875rem',
        fontWeight: 500,
        fontFamily: theme.typography.fontFamily,
        height: '48px',
        backgroundColor: theme.palette.background.paper,
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.main,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderWidth: '1px',
          borderColor: theme.palette.primary.main,
        },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.error.main,
        },
      },
      select: {
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'break-spaces',
      },
      icon: {
        color: theme.palette.text.secondary,
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      fullWidth: true,
    },
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: '4px',
          transition: 'all .2s ease-in-out',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1px',
            borderColor: theme.palette.primary.main,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.error.main,
          },
        },
        '& .MuiInputLabel-root': {
          fontFamily: theme.typography.fontFamily,
          fontSize: '.875rem',
          '&.Mui-focused': {
            color: theme.palette.primary.main,
          },
          '&.Mui-error': {
            color: theme.palette.error.main,
          },
        },
        '& .MuiFormHelperText-root': {
          marginLeft: 0,
          fontSize: '.75rem',
          '&.Mui-error': {
            color: theme.palette.error.main,
          },
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        fontSize: '.875rem',
        fontWeight: 500,
        fontFamily: theme.typography.fontFamily,
        height: '48px',
        color: theme.palette.text.primary,
        // Multiline textareas need auto height; the fixed 48px is for single-line inputs.
        '&.MuiInputBase-multiline': {
          height: 'auto',
          minHeight: 'auto',
          alignItems: 'flex-start',
          padding: 0,
        },
        // Date input error state
        '&.Mui-error input[type="date"]::-webkit-datetime-edit': {
          color: theme.palette.error.main,
        },
        // Disabled state: use action.disabled instead of text.disabled so the
        // value text remains legible (text.disabled is intentionally light for labels)
        '&.Mui-disabled': {
          '& .MuiOutlinedInput-input': {
            '-webkit-text-fill-color': theme.palette.action.disabled,
          },
        },
      },
      input: {
        padding: '12px 14px',
        // Date input styling - empty state
        '&[type="date"]::-webkit-datetime-edit': {
          color: theme.palette.text.secondary,
        },
        // Date input styling - filled state
        '&[type="date"]:valid::-webkit-datetime-edit': {
          color: theme.palette.text.primary,
        },
      },
      notchedOutline: {
        borderColor:
          theme.palette.mode === 'light'
            ? theme.palette.grey[200]
            : theme.palette.divider,
        // WebKit/Safari: legend used for the notch can render badly (label overlap, ghost box on blur).
        // Forcing visibility fixes the underlying notched-outline rendering.
        // Reference: https://github.com/mui/material-ui/issues/46891#issuecomment-3291920760
        '@supports (-webkit-appearance: none)': {
          '& legend': {
            visibility: 'visible !important',
          },
        },
      },
    },
  },
  MuiPickersOutlinedInput: {
    root: {
      fontSize: '.875rem',
      fontWeight: 500,
      fontFamily: theme.typography.fontFamily,
      height: '48px',
      color: theme.palette.text.primary,
      // Date input error state
      '&.Mui-error input[type="date"]::-webkit-datetime-edit': {
        color: theme.palette.error.main,
      },
    },
    styleOverrides: {
      sectionsContainer: {
        width: '100%',
        fontSize: '.875rem',
        padding: '12.5px 0',
      },
      notchedOutline: {
        borderColor:
          theme.palette.mode === 'light'
            ? theme.palette.grey[200]
            : theme.palette.divider,
        // WebKit/Safari: legend used for the notch can render badly (label overlap, ghost box on blur).
        // Forcing visibility fixes the underlying notched-outline rendering.
        // Reference: https://github.com/mui/material-ui/issues/46891#issuecomment-3291920760
        '@supports (-webkit-appearance: none)': {
          '& legend': {
            visibility: 'visible !important',
          },
        },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        fontSize: '.875rem',
        fontWeight: 500,
        color: theme.palette.text.secondary,
        transform: 'translate(14px, 14px) scale(1)',
        '&.Mui-focused': {
          transform: 'translate(14px, -9px) scale(.75)',
        },
        '&.MuiInputLabel-shrink': {
          transform: 'translate(14px, -9px) scale(.75)',
        },
      },
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        fontSize: '.875rem',
        fontWeight: 500,
        color: theme.palette.text.secondary,
      },
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      root: {
        marginLeft: 0,
      },
      label: {
        fontSize: '.875rem',
        fontWeight: 500,
        color: theme.palette.text.primary,
      },
    },
  },
  MuiRadio: {
    styleOverrides: {
      root: {
        padding: 0,
        marginRight: '10px',
      },
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: {
        padding: 0,
        marginRight: '10px',
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        marginTop: '5px',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, .1)',
        backgroundColor: theme.palette.background.paper,
      },
      list: {
        padding: '4px 0',
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontSize: theme.typography.body2.fontSize,
        fontFamily: theme.typography.fontFamily,
        height: '43px',
        padding: '6px 16px',
        color: theme.palette.text.primary,
        '&.Mui-selected': {
          backgroundColor: theme.palette.action.selected,
        },
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        marginLeft: 0,
        fontSize: '.75rem',
        '&.Mui-error': {
          color: theme.palette.error.main,
        },
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      root: {
        '& .MuiInputBase-root': {
          minHeight: 48,
          height: 'auto',
          alignItems: 'center',
          flexWrap: 'wrap',
          padding: '6px 14px',
        },
        '& .MuiInputBase-input': {
          minWidth: '30px !important',
          padding: '0px',
        },
      },
    },
    slotProps: {
      chip: {
        size: 'small',
        sx: {
          height: '24px',
          '& .MuiChip-label': {
            fontSize: theme.typography.body2.fontSize,
          },
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        fontWeight: 600,
        fontSize: theme.typography.body2.fontSize,
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.action.hover,
      },
    },
  },
});

// Now apply the component styles to both themes
lightTheme.components = getThemeComponentStyles(lightTheme);
darkTheme.components = getThemeComponentStyles(darkTheme);

// Add any light theme specific overrides
lightTheme.components = {
  ...lightTheme.components,
  MuiTextField: {
    ...lightTheme.components?.MuiTextField,
    styleOverrides: {
      ...lightTheme.components?.MuiTextField?.styleOverrides,
      root: {
        ...lightTheme.components?.MuiTextField?.styleOverrides,
      },
    },
  },
  MuiSelect: {
    ...lightTheme.components?.MuiSelect,
    styleOverrides: {
      ...lightTheme.components?.MuiSelect?.styleOverrides,
      root: {
        ...lightTheme.components?.MuiSelect?.styleOverrides,
      },
    },
  },
};

// Add any dark theme specific overrides
darkTheme.components = {
  ...darkTheme.components,
  MuiTextField: {
    ...darkTheme.components?.MuiTextField,
    styleOverrides: {
      ...darkTheme.components?.MuiTextField?.styleOverrides,
      root: {
        ...darkTheme.components?.MuiTextField?.styleOverrides,
      },
    },
  },
  MuiSelect: {
    ...darkTheme.components?.MuiSelect,
    styleOverrides: {
      ...darkTheme.components?.MuiSelect?.styleOverrides,
      root: {
        ...darkTheme.components?.MuiSelect?.styleOverrides,
      },
    },
  },
};
